import { connectorInvoke, isConnectorAvailable } from '../../lib/connector';
import {
  normalizeLocalAlbumId,
  normalizeLocalArtistId,
  normalizeLocalTrackId,
  localAlbumIpcId,
  localArtistIpcId,
  localTrackIpcId,
} from '../localLibraryMetadata';
import {
  LocalLibraryAdapter,
  LocalLibraryListArgs,
  LocalLibraryListResponse,
  AlbumSummary,
  ArtistSummary,
  TrackSummary,
  AlbumDetail,
  ArtistDetail,
  TrackDetail,
  SearchResults,
  LocalLibrarySearchArgs,
  LibraryDiffResponse,
  LibraryStats,
  CoverStats,
  SourceItem,
  SourceMutation,
  LocalLibraryCapabilities,
  LocalLibraryHealthStatus,
} from './LocalLibraryAdapter';

type TauriInvoke = <T = any>(command: string, args?: Record<string, unknown>) => Promise<T>;

/** Slightly longer than Connector `GET_ALBUMS_COMMAND_TIMEOUT` (20s) so backend errors surface first. */
export const LOCAL_LIBRARY_GET_ALBUMS_TIMEOUT_MS = 25_000;

function invokeWithTimeout<T>(
  invoke: () => Promise<T>,
  ms: number,
  label: string,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error(`${label} timed out after ${Math.round(ms / 1000)}s`));
    }, ms);
    invoke().then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

function getTauriInvoke(): TauriInvoke {
  if (!isConnectorAvailable()) {
    throw new Error('Tauri runtime unavailable');
  }
  return connectorInvoke;
}

function normalizeListArgs(args: LocalLibraryListArgs) {
  return {
    offset: args.offset ?? 0,
    limit: args.limit ?? 200,
    sort: args.sort ?? null,
    filter: args.filter ?? null,
    sourceId: args.sourceId ?? null,
  };
}

function mapAlbum(item: any): AlbumSummary {
  const bareId = normalizeLocalAlbumId(item?.id || item?.album_key || '');
  const provisional = item?.provisional === true;
  const placeholder = item?.placeholder === true
    || (provisional && !item?.cover_art_thumb_hash && !item?.coverArtThumbHash);
  // Provisional snapshot tiles omit track_count — keep them visible in AlbumGrid
  // (grid filters track_count === 0).
  const trackCount = provisional
    ? Math.max(1, Number(item?.track_count || 1))
    : Number(item?.track_count || 0);
  return {
    id: bareId,
    album_key: bareId,
    title: String(item?.title || ''),
    artist_name: String(item?.artist_name || ''),
    year: item?.year ?? null,
    genre: item?.genre ?? null,
    track_count: trackCount,
    total_duration: Number(item?.total_duration || 0),
    cover_art_url: item?.cover_art_url ?? null,
    cover_art_thumb_hash: item?.cover_art_thumb_hash ?? item?.coverArtThumbHash ?? null,
    embedded_cover_hash: item?.embedded_cover_hash ?? item?.embeddedCoverHash ?? null,
    folder_cover_hash: item?.folder_cover_hash ?? item?.folderCoverHash ?? null,
    external_cover_hash: item?.external_cover_hash ?? item?.externalCoverHash ?? null,
    library_folder_path: item?.library_folder_path ?? item?.libraryFolderPath ?? null,
    sample_file_path: item?.sample_file_path ?? item?.sampleFilePath ?? null,
    format: item?.format ?? null,
    bit_depth: item?.bit_depth ?? null,
    sample_rate: item?.sample_rate ?? null,
    created_at: item?.created_at ?? null,
    updated_at: item?.updated_at ?? null,
    source_id: item?.source_id ?? null,
    machine_id: item?.machine_id ?? null,
    version_count: Number(item?.version_count ?? 1),
    versions: Array.isArray(item?.versions) ? item.versions : undefined,
    provisional: provisional || undefined,
    placeholder: placeholder || undefined,
  };
}

function mapArtist(item: any): ArtistSummary {
  const bareId = normalizeLocalArtistId(item?.id || '');
  return {
    id: bareId,
    name: String(item?.name || ''),
    album_count: item?.album_count ?? item?.albumCount ?? 0,
    track_count: item?.track_count ?? item?.trackCount ?? 0,
    image_url: item?.image_url ?? null,
    image_source: item?.image_source ?? null,
  };
}

function mapTrack(item: any): TrackSummary {
  const bareId = normalizeLocalTrackId(item?.id || '');
  return {
    id: bareId,
    title: String(item?.title || ''),
    artist: item?.artist ?? null,
    artist_name: item?.artist_name ?? item?.artist ?? null,
    album: item?.album ?? item?.album_title ?? null,
    album_title: item?.album_title ?? item?.album ?? null,
    duration: item?.duration ?? null,
    format: item?.format ?? null,
    bit_depth: item?.bit_depth ?? null,
    sample_rate: item?.sample_rate ?? null,
    file_path: item?.file_path ?? null,
    machine_id: item?.machine_id ?? null,
    track_number: item?.track_number ?? null,
    disc_number: item?.disc_number ?? null,
  };
}

export class TauriLocalLibraryAdapter implements LocalLibraryAdapter {
  private invoke: TauriInvoke;

  constructor() {
    this.invoke = getTauriInvoke();
  }

  async getAlbums(args: LocalLibraryListArgs): Promise<LocalLibraryListResponse<AlbumSummary>> {
    const payload: any = await invokeWithTimeout(
      () => this.invoke('local_library_get_albums', {
        params: normalizeListArgs(args),
      }),
      LOCAL_LIBRARY_GET_ALBUMS_TIMEOUT_MS,
      'local_library_get_albums',
    );
    const items = (payload?.albums || payload?.items || []).map(mapAlbum);
    return {
      items,
      total: Number(payload?.total_count ?? payload?.total ?? items.length),
      totalReleases: Number(payload?.total_releases ?? payload?.totalReleases ?? 0) || undefined,
      version: Number(payload?.version || 0),
      provisional: payload?.provisional === true,
      updating: payload?.updating === true || payload?.provisional === true,
      catalog_live: payload?.catalog_live === true,
      snapshot_stale: payload?.snapshot_stale === true,
      snapshot_captured_at: typeof payload?.snapshot_captured_at === 'string'
        ? payload.snapshot_captured_at
        : null,
      snapshot_album_count: payload?.snapshot_album_count != null
        ? Number(payload.snapshot_album_count)
        : null,
    };
  }

  async getAlbum(id: string): Promise<AlbumDetail> {
    const payload: any = await this.invoke('local_library_get_album', { id: localAlbumIpcId(id) });
    return {
      success: !!payload?.success,
      album: mapAlbum(payload?.album || {}),
      tracks: (payload?.tracks || []).map(mapTrack),
      version: Number(payload?.version || 0),
    };
  }

  async getArtists(args: LocalLibraryListArgs): Promise<LocalLibraryListResponse<ArtistSummary>> {
    const payload: any = await this.invoke('local_library_get_artists', {
      params: normalizeListArgs(args),
    });
    const items = (payload?.artists || payload?.items || []).map(mapArtist);
    return {
      items,
      total: Number(payload?.total || items.length),
      version: Number(payload?.version || 0),
    };
  }

  async getArtist(id: string): Promise<ArtistDetail> {
    const payload: any = await this.invoke('local_library_get_artist', { id: localArtistIpcId(id) });
    return {
      success: !!payload?.success,
      artist: mapArtist(payload?.artist || {}),
      version: Number(payload?.version || 0),
    };
  }

  async getTracks(args: LocalLibraryListArgs): Promise<LocalLibraryListResponse<TrackSummary>> {
    const payload: any = await this.invoke('local_library_get_tracks', {
      params: normalizeListArgs(args),
    });
    const items = (payload?.tracks || payload?.items || []).map(mapTrack);
    return {
      items,
      total: Number(payload?.total || items.length),
      version: Number(payload?.version || 0),
    };
  }

  async getTrack(id: string): Promise<TrackDetail> {
    const payload: any = await this.invoke('local_library_get_track', { id: localTrackIpcId(id) });
    return {
      success: !!payload?.success,
      track: mapTrack(payload?.track || {}),
      version: Number(payload?.version || 0),
    };
  }

  async search(args: LocalLibrarySearchArgs): Promise<SearchResults> {
    const payload: any = await this.invoke('local_library_search', {
      params: {
        query: args.query,
        scope: args.scope ?? 'all',
        limit: args.limit ?? 100,
        offset: args.offset ?? 0,
      },
    });
    return {
      artists: (payload?.artists || []).map(mapArtist),
      albums: (payload?.albums || []).map(mapAlbum),
      tracks: (payload?.tracks || []).map(mapTrack),
    };
  }

  async getLibraryVersion(): Promise<number> {
    const payload: any = await this.invoke('local_library_get_version');
    return Number(payload?.version || 0);
  }

  async getLibraryDiff(sinceVersion: number): Promise<LibraryDiffResponse> {
    const payload: any = await this.invoke('local_library_get_diff', { sinceVersion });
    return {
      added: payload?.added || [],
      updated: payload?.updated || [],
      removed: payload?.removed || [],
      fullResync: payload?.fullResync ? true : undefined,
      version: Number(payload?.version || 0),
    };
  }

  async getStats(): Promise<LibraryStats> {
    const payload: any = await this.invoke('local_library_get_stats');
    return {
      albums: Number(payload?.albums || payload?.total_albums || 0),
      artists: Number(payload?.artists || 0),
      tracks: Number(payload?.tracks || payload?.total_tracks || 0),
      totalDurationSec: Number(payload?.total_duration_sec || 0),
      totalSizeBytes: Number(payload?.total_size_bytes || 0),
      byFormat: Array.isArray(payload?.by_format)
        ? payload.by_format.map((row: any) => ({
            format: String(row?.format || 'unknown'),
            count: Number(row?.count || 0),
          }))
        : [],
      emptySourcesCount: Number(
        payload?.empty_sources_count ?? payload?.emptySourcesCount ?? 0,
      ),
      emptySourceLabels: Array.isArray(payload?.empty_source_labels)
        ? payload.empty_source_labels.map((label: unknown) => String(label || '')).filter(Boolean)
        : Array.isArray(payload?.emptySourceLabels)
          ? payload.emptySourceLabels.map((label: unknown) => String(label || '')).filter(Boolean)
          : [],
    };
  }

  async getSourceSummary(sourceId: string) {
    const payload: any = await this.invoke('local_library_get_source_summary', { sourceId });
    return {
      albumCount: Number(payload?.albumCount ?? payload?.album_count ?? 0),
      trackCount: Number(payload?.trackCount ?? payload?.track_count ?? 0),
      coverArtUrl: payload?.coverArtUrl ?? payload?.cover_art_url ?? null,
    };
  }

  async getCoverStats(): Promise<CoverStats> {
    const payload: any = await this.invoke('local_library_get_cover_stats');
    return {
      total: Number(payload?.total || 0),
      withCover: Number(payload?.withCover || 0),
      withoutCover: Number(payload?.withoutCover || 0),
    };
  }

  async listSources(): Promise<SourceItem[]> {
    const payload: any = await this.invoke('local_library_list_sources');
    return (payload?.sources || []).map((s: any) => ({
      id: String(s?.id || ''),
      path: String(s?.path || ''),
      label: String(s?.label || ''),
      source_type: s?.source_type || 'local',
      machine_id: s?.machine_id ?? null,
      status: s?.status || 'online',
    }));
  }

  async addSource(input: SourceMutation): Promise<{ source: SourceItem; reused?: boolean }> {
    const payload: any = await this.invoke('local_library_add_source', { input });
    return {
      source: {
        id: String(payload?.source?.id || ''),
        path: String(payload?.source?.path || ''),
        label: String(payload?.source?.label || ''),
        source_type: payload?.source?.source_type || 'local',
        machine_id: payload?.source?.machine_id ?? null,
        status: payload?.source?.status || 'online',
      },
      reused: !!payload?.reused,
    };
  }

  async addSources(inputs: SourceMutation[]): Promise<{
    success: boolean;
    results: Array<{ success?: boolean; reused?: boolean; source?: SourceItem }>;
    count: number;
  }> {
    const payload: any = await this.invoke('local_library_add_sources', {
      input: { sources: inputs },
    });
    const results = (payload?.results || []).map((r: any) => ({
      success: !!r?.success,
      reused: !!r?.reused,
      source: r?.source ? {
        id: String(r.source?.id || ''),
        path: String(r.source?.path || ''),
        label: String(r.source?.label || ''),
        source_type: r.source?.source_type || 'local',
        machine_id: r.source?.machine_id ?? null,
        status: r.source?.status || 'online',
      } : undefined,
    }));
    return {
      success: !!payload?.success,
      results,
      count: Number(payload?.count ?? results.length),
    };
  }

  async removeSource(id: string, clearCatalog = true): Promise<{ removed: number }> {
    const payload: any = await this.invoke('local_library_remove_source', { id, clearCatalog });
    return { removed: Number(payload?.removed || 0) };
  }

  async rescanSource(id: string): Promise<{ source_id: string; queued: boolean }> {
    const payload: any = await this.invoke('local_library_rescan_source', { id });
    return {
      source_id: String(payload?.source_id || id),
      queued: !!payload?.queued,
    };
  }

  async getCapabilities(): Promise<LocalLibraryCapabilities> {
    const payload: any = await this.invoke('local_library_get_capabilities');
    return {
      enrichment: !!payload?.enrichment,
      bulkRepair: !!payload?.bulkRepair,
      duplicateManagement: !!payload?.duplicateManagement,
      folderManagement: !!payload?.folderManagement,
      scanStatus: !!payload?.scanStatus,
      artworkManagement: !!payload?.artworkManagement,
    };
  }

  async getHealthStatus(): Promise<LocalLibraryHealthStatus> {
    const payload: any = await this.invoke('local_library_get_health_status');
    return {
      status: (payload?.status || 'unknown') as LocalLibraryHealthStatus['status'],
      message: payload?.message ?? null,
      lastRunAt: payload?.lastRunAt ?? null,
      mappedLegacyCount: payload?.mappedLegacyCount ?? null,
      unresolvedLegacyCount: payload?.unresolvedLegacyCount ?? null,
    };
  }

  async deleteAlbum(id: string) {
    const ipcId = localAlbumIpcId(id);
    const payload: any = await this.invoke('local_library_delete_album', { id: ipcId });
    return {
      albumId: String(payload?.albumId || payload?.album_id || normalizeLocalAlbumId(id)),
      tracksDeleted: Number(payload?.tracksDeleted || payload?.tracks_deleted || 0),
      catalogVersion: Number(payload?.catalogVersion || payload?.catalog_version || 0),
    };
  }

  async rereadAlbum(id: string) {
    const ipcId = localAlbumIpcId(id);
    const payload: any = await this.invoke('local_library_reread_album', { id: ipcId });
    return {
      success: payload?.success !== false,
      albumId: String(payload?.albumId || payload?.album_id || normalizeLocalAlbumId(id)),
      filesUpdated: Number(payload?.filesUpdated || payload?.files_updated || 0),
      catalogVersion: Number(payload?.catalogVersion || payload?.catalog_version || 0),
    };
  }

  async removeAlbum(id: string) {
    const ipcId = localAlbumIpcId(id);
    const payload: any = await this.invoke('local_library_remove_album', { id: ipcId });
    return {
      albumId: String(payload?.albumId || payload?.album_id || normalizeLocalAlbumId(id)),
      tracksDeleted: Number(payload?.tracksDeleted || payload?.tracks_deleted || 0),
      filesDeleted: Number(payload?.filesDeleted || payload?.files_deleted || 0),
      scanJobsCleared: Number(payload?.scanJobsCleared || payload?.scan_jobs_cleared || 0),
      artworkPruned: Number(payload?.artworkPruned || payload?.artwork_pruned || 0),
      catalogVersion: Number(payload?.catalogVersion || payload?.catalog_version || 0),
    };
  }

  async clearSource(sourceId: string) {
    const payload: any = await this.invoke('local_library_clear_source', { sourceId });
    return {
      sourceId: String(payload?.sourceId || payload?.source_id || sourceId),
      albumsDeleted: Number(payload?.albumsDeleted || payload?.albums_deleted || 0),
      tracksDeleted: Number(payload?.tracksDeleted || payload?.tracks_deleted || 0),
      catalogVersion: Number(payload?.catalogVersion || payload?.catalog_version || 0),
    };
  }

  async wipeAll() {
    const payload: any = await this.invoke('local_library_wipe_all');
    return {
      albumsDeleted: Number(payload?.albumsDeleted || payload?.albums_deleted || 0),
      tracksDeleted: Number(payload?.tracksDeleted || payload?.tracks_deleted || 0),
      catalogVersion: Number(payload?.catalogVersion || payload?.catalog_version || 0),
    };
  }

  async resetCatalog() {
    const payload: any = await this.invoke('local_library_reset_catalog');
    return {
      albumsDeleted: Number(payload?.albumsDeleted || payload?.albums_deleted || 0),
      tracksDeleted: Number(payload?.tracksDeleted || payload?.tracks_deleted || 0),
      catalogVersion: Number(payload?.catalogVersion || payload?.catalog_version || 0),
    };
  }

  async removeAll() {
    const payload: any = await this.invoke('local_library_remove_all');
    return {
      sourcesRemoved: Number(payload?.sourcesRemoved || payload?.sources_removed || 0),
      albumsDeleted: Number(payload?.albumsDeleted || payload?.albums_deleted || 0),
      tracksDeleted: Number(payload?.tracksDeleted || payload?.tracks_deleted || 0),
      filesDeleted: Number(payload?.filesDeleted || payload?.files_deleted || 0),
      catalogVersion: Number(payload?.catalogVersion || payload?.catalog_version || 0),
    };
  }
}
